import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock3, Images, MapPin, Play, Search, SlidersHorizontal } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Nav from "../components/Nav";
import api, { API, thumbUrl } from "../lib/api";
import { useToast } from "../lib/toast";
import "./events-gallery.css";
import { track } from "../analytics/tracker";

const PAGE_SIZE = 12;

function formatDate(value, options = { day: "numeric", month: "short", year: "numeric" }) {
  if (!value) return "Date not listed";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", options);
}

function EventCover({ event }) {
  const cover = event.media.find((item) => item.mime?.startsWith("image/")) || event.media[0];
  return (
    <div className="eg-cover">
      {cover && (cover.mime?.startsWith("image/") || cover.mime?.startsWith("video/")) ? (
        <img
          src={thumbUrl(cover.id)}
          alt=""
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : null}
      <div className="eg-cover-fallback" aria-hidden="true">
        <Images size={34} />
        <span>{event.event_type || "Event highlights"}</span>
      </div>
      <div className="eg-cover-shade" />
      <span className="eg-cover-play" aria-hidden="true"><Play size={17} fill="currentColor" /></span>
      <span className="eg-cover-count">{event.media.length} {event.media.length === 1 ? "highlight" : "highlights"}</span>
      <span className="eg-cover-date">{formatDate(event.event_date, { day: "numeric", month: "short" })}</span>
    </div>
  );
}

function EventCard({ event }) {
  return (
    <Link className="eg-event-card" to={`/events-gallery/${encodeURIComponent(event.id)}`} data-testid={`gallery-event-${event.id}`}>
      <EventCover event={event} />
      <div className="eg-card-copy">
        <span className="eg-event-type">{event.event_type || "Event"}</span>
        <h2>{event.title || `${event.event_type || "Event"} highlights`}</h2>
        <p className="eg-card-meta">
          {event.artists.map((artist) => artist.name).join(", ")}
          {event.artists.length > 0 && event.city ? " · " : ""}
          {event.city}
        </p>
        <div className="eg-card-counts">
          <span><Play size={13} /> {event.video_count} videos</span>
          <span><Images size={13} /> {event.photo_count} photos</span>
        </div>
      </div>
    </Link>
  );
}

function EventsList({ events, loading, error }) {
  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("All events");
  const [city, setCity] = useState("All cities");
  const [month, setMonth] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const eventTypes = useMemo(
    () => ["All events", ...new Set(events.map((event) => event.event_type).filter(Boolean).sort())],
    [events],
  );
  const cities = useMemo(
    () => ["All cities", ...new Set(events.map((event) => event.city).filter(Boolean).sort())],
    [events],
  );
  const filteredEvents = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return events
      .filter((event) => eventType === "All events" || event.event_type === eventType)
      .filter((event) => city === "All cities" || event.city === city)
      .filter((event) => !month || String(event.event_date || "").startsWith(month))
      .filter((event) => !needle ||
        event.artists.some((artist) => artist.name.toLowerCase().includes(needle)) ||
        event.title?.toLowerCase().includes(needle) ||
        event.event_type?.toLowerCase().includes(needle) ||
        event.city?.toLowerCase().includes(needle))
      .sort((a, b) => {
        if (sort === "oldest") return String(a.event_date).localeCompare(String(b.event_date));
        if (sort === "recent-media") return String(b.latest_media_at).localeCompare(String(a.latest_media_at));
        return String(b.event_date).localeCompare(String(a.event_date));
      });
  }, [events, search, eventType, city, month, sort]);

  useEffect(() => { setPage(1); }, [search, eventType, city, month, sort]);

  const pages = Math.max(1, Math.ceil(filteredEvents.length / PAGE_SIZE));
  const visibleEvents = filteredEvents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <main className="eg-main">
      <section className="eg-hero">
        <h1>Real events, real performances.<br /><em>Watch before you book.</em></h1>
      </section>

      <section className="eg-library" aria-label="Browse event media">
        <div className="eg-library-heading">
          <div>
            <h2>Events worth <span>replaying</span></h2>
          </div>
          <div className="eg-sort">
            <SlidersHorizontal size={15} aria-hidden="true" />
            <label htmlFor="eg-sort">Timeline</label>
            <select id="eg-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">Newest events</option>
              <option value="oldest">Oldest events</option>
              <option value="recent-media">Recently added</option>
            </select>
          </div>
        </div>

        <div className="eg-toolbar">
          <div className="eg-type-filters" aria-label="Filter by event type">
            {eventTypes.map((type) => (
              <button
                type="button"
                key={type}
                className={`eg-chip ${eventType === type ? "is-active" : ""}`}
                onClick={() => setEventType(type)}
              >
                {type}
              </button>
            ))}
          </div>
          <div className="eg-filter-controls">
            <label className="eg-search">
              <Search size={16} aria-hidden="true" />
              <input
                type="search"
                aria-label="Search artist, city, or event"
                placeholder="Search artist or event"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <label className="eg-select">
              <MapPin size={15} aria-hidden="true" />
              <select aria-label="Filter by city" value={city} onChange={(e) => setCity(e.target.value)}>
                {cities.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label className="eg-date">
              <CalendarDays size={15} aria-hidden="true" />
              <input type="month" aria-label="Filter by event month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </label>
          </div>
        </div>

        <div className="eg-results-line">
          <p>{filteredEvents.length} {filteredEvents.length === 1 ? "event" : "events"}</p>
          <span>Showing published event highlights</span>
        </div>

        {loading ? (
          <div className="eg-state" role="status">Loading event gallery…</div>
        ) : error ? (
          <div className="eg-state eg-state-error" role="alert">{error}</div>
        ) : visibleEvents.length ? (
          <>
            <section className="eg-grid" aria-label="Events">
              {visibleEvents.map((event) => <EventCard key={event.id} event={event} />)}
            </section>
            {pages > 1 && (
              <nav className="eg-pagination" aria-label="Gallery pages">
                <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} aria-label="Previous page">‹</button>
                {Array.from({ length: pages }, (_, index) => index + 1).map((number) => (
                  <button
                    type="button"
                    key={number}
                    className={number === page ? "is-current" : ""}
                    aria-current={number === page ? "page" : undefined}
                    onClick={() => setPage(number)}
                  >{number}</button>
                ))}
                <button type="button" disabled={page === pages} onClick={() => setPage(page + 1)} aria-label="Next page">›</button>
              </nav>
            )}
          </>
        ) : (
          <div className="eg-empty">
            <Images size={30} />
            <h3>No events match those filters</h3>
            <p>Try another artist, city, event type, or month.</p>
            <button type="button" className="eg-reset" onClick={() => {
              setSearch("");
              setEventType("All events");
              setCity("All cities");
              setMonth("");
            }}>Clear filters</button>
          </div>
        )}
      </section>

      <section className="eg-cta">
        <div><span className="eg-eyebrow">YOUR MOMENT, NEXT</span><h2>Ready to make an entrance?</h2><p>Find the artist who will make your event unforgettable.</p></div>
        <Link to="/search" className="eg-cta-button">Browse artists <span aria-hidden="true">→</span></Link>
      </section>
    </main>
  );
}

function EventDetail({ event, loading, error, onBack, relatedEvents }) {
  const toast = useToast();
  const [selectedVideoId, setSelectedVideoId] = useState("");
  const [tab, setTab] = useState("photos");
  const videos = event?.media.filter((item) => item.mime?.startsWith("video/")) || [];
  const photos = event?.media.filter((item) => item.mime?.startsWith("image/")) || [];
  const selectedVideo = videos.find((item) => item.id === selectedVideoId) || videos[0];

  useEffect(() => {
    setSelectedVideoId("");
    setTab("photos");
  }, [event?.id]);

  const shareEvent = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast("Event link copied.", "success");
    } catch {
      toast("Could not copy the event link.", "error");
    }
  };

  if (loading) return <main className="eg-main"><div className="eg-state">Loading event…</div></main>;
  if (error || !event) {
    return (
      <main className="eg-main">
        <div className="eg-state eg-state-error">{error || "Event not found"}</div>
        <Link className="eg-back" to="/events-gallery"><ArrowLeft size={15} /> Back to Events Gallery</Link>
      </main>
    );
  }

  const cover = photos[0] || event.media[0];
  return (
    <main className="eg-main eg-detail">
      <div className="eg-crumb"><Link to="/events-gallery">Events Gallery</Link><span>/</span><span>{event.event_type}</span><span>/</span><b>{event.title}</b></div>
      <div className="eg-event-head">
        <div>
          <span className="eg-eyebrow">EVENT HIGHLIGHTS</span>
          <h1>{event.title || `${event.event_type} highlights`}</h1>
          <div className="eg-event-meta">
            <span><MapPin size={15} />{event.city || "Location not listed"}</span>
            <span><CalendarDays size={15} />{formatDate(event.event_date)}</span>
            <span>{event.event_type}</span>
            <span><Play size={14} />{videos.length} videos · {photos.length} photos</span>
          </div>
        </div>
        <button type="button" className="eg-share" onClick={shareEvent}>Share event</button>
      </div>

      {videos.length > 0 ? (
        <section className="eg-player-layout" aria-label="Event videos">
          <div className="eg-player">
            <video key={selectedVideo?.id} src={`${API}/media/${encodeURIComponent(selectedVideo?.id)}/file`} controls playsInline preload="metadata" />
            <div className="eg-player-caption">
              <strong>{selectedVideo?.title || "Event highlight"}</strong>
              <span>{event.artists.map((artist) => artist.name).join(" · ")}</span>
            </div>
          </div>
          <aside className="eg-playlist">
            <h2>Videos <span>{videos.length}</span></h2>
            {videos.map((video) => (
              <button
                type="button"
                className={`eg-playlist-item ${selectedVideo?.id === video.id ? "is-playing" : ""}`}
                key={video.id}
                onClick={() => setSelectedVideoId(video.id)}
              >
                <span className="eg-playlist-thumb"><Play size={15} fill="currentColor" /></span>
                <span><strong>{video.title || "Event video"}</strong><small>{event.artists.map((artist) => artist.name).join(", ")}</small></span>
              </button>
            ))}
          </aside>
        </section>
      ) : (
        <section className="eg-feature-photo">
          {cover && <img src={thumbUrl(cover.id)} alt={event.title || "Event highlight"} />}
          <div><Images size={26} /><span>Photo highlights from {event.event_type}</span></div>
        </section>
      )}

      <div className="eg-artists-bar">
        <strong>Artists</strong>
        {event.artists.map((artist) => (
          <Link className="eg-artist-pill" to={`/artist/${encodeURIComponent(artist.id)}`} key={artist.id} onClick={() => track("artist_card_click", { artist_id: artist.id, source: "gallery" })}>
            {artist.featured_media_id
              ? <img src={thumbUrl(artist.featured_media_id)} alt="" />
              : <span className="eg-artist-initial">{artist.name.slice(0, 1)}</span>}
            <span>{artist.name}</span>
            {artist.category && <small>{artist.category}</small>}
          </Link>
        ))}
        <Link to="/search" className="eg-availability">Find an artist <span aria-hidden="true">→</span></Link>
      </div>

      <div className="eg-tabs" role="tablist" aria-label="Event media type">
        <button type="button" role="tab" aria-selected={tab === "photos"} className={tab === "photos" ? "is-active" : ""} onClick={() => setTab("photos")}>Photos <span>{photos.length}</span></button>
        <button type="button" role="tab" aria-selected={tab === "videos"} className={tab === "videos" ? "is-active" : ""} onClick={() => setTab("videos")}>Videos <span>{videos.length}</span></button>
      </div>
      {tab === "photos" ? (
        photos.length ? (
          <section className="eg-photos" aria-label="Event photos">
            {photos.map((photo, index) => (
              <a href={`${API}/media/${encodeURIComponent(photo.id)}/file`} target="_blank" rel="noreferrer" className={`eg-photo eg-photo-${index % 4}`} key={photo.id}>
                <img src={thumbUrl(photo.id)} alt={photo.title || `Event photo ${index + 1}`} loading="lazy" />
              </a>
            ))}
          </section>
        ) : <div className="eg-state eg-no-media">No photos were published for this event.</div>
      ) : (
        videos.length ? (
          <section className="eg-video-grid" aria-label="Event videos">
            {videos.map((video) => (
              <button type="button" className="eg-video-card" key={video.id} onClick={() => {
                setSelectedVideoId(video.id);
                document.querySelector(".eg-player")?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}>
                <span className="eg-video-card-thumb"><Play size={21} fill="currentColor" /></span>
                <strong>{video.title || "Event video"}</strong>
              </button>
            ))}
          </section>
        ) : <div className="eg-state eg-no-media">No videos were published for this event.</div>
      )}

      <section className="eg-detail-bottom">
        <div><Clock3 size={16} /><span>Event timeline</span><strong>{formatDate(event.event_date)}</strong></div>
        <button type="button" className="eg-back-button" onClick={onBack}><ArrowLeft size={15} /> All events</button>
      </section>
      {relatedEvents.length > 0 && (
        <section className="eg-related">
          <div className="eg-library-heading"><div><span className="eg-eyebrow">KEEP EXPLORING</span><h2>More event stories</h2></div></div>
          <div className="eg-grid">{relatedEvents.slice(0, 4).map((item) => <EventCard key={item.id} event={item} />)}</div>
        </section>
      )}
    </main>
  );
}

export default function EventsGallery() {
  const { event_id: eventId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/events-gallery")
      .then((response) => {
        if (!cancelled) setEvents((response.data?.events || []).map((event) => ({
          ...event,
          title: event.title || `${event.event_type || "Event"} in ${event.city || "India"}`,
          latest_media_at: event.media.reduce((latest, item) => item.created_at > latest ? item.created_at : latest, ""),
        })));
      })
      .catch(() => { if (!cancelled) setError("We could not load the Events Gallery. Please try again."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!eventId) {
      setSelectedEvent(null);
      setError("");
      return undefined;
    }
    let cancelled = false;
    setDetailLoading(true);
    setError("");
    setSelectedEvent(null);
    api.get(`/events-gallery/${encodeURIComponent(eventId)}`)
      .then((response) => { if (!cancelled) setSelectedEvent(response.data?.event || null); })
      .catch((requestError) => {
        if (!cancelled) setError(requestError?.response?.status === 404 ? "This event is not available in the public gallery." : "We could not load this event.");
      })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [eventId]);

  const pageTitle = selectedEvent
    ? `${selectedEvent.event_type || "Event"} Highlights · BookTalent`
    : "Events Gallery · BookTalent";

  return (
    <div className="events-gallery-page" data-testid="events-gallery-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Explore published event photos and videos from BookTalent artists. Search by artist, event type, city, and date." />
      </Helmet>
      <Nav />
      {eventId
        ? <EventDetail
            event={selectedEvent}
            loading={detailLoading}
            error={error}
            onBack={() => navigate("/events-gallery")}
            relatedEvents={events.filter((event) => event.id !== eventId)}
          />
        : <EventsList events={events} loading={loading} error={error} />}
    </div>
  );
}
